import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { BILLING_ENABLED } from '@constants/features';
import { MantineProvider, createTheme, Loader } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@libs/query-client';
import { lazy, Suspense, useEffect, useMemo } from 'react';
import { MFAProvider } from './contexts/mfa-context';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { TextScaleProvider } from './contexts/TextScaleContext';
import AppUpdateNavigationWatcher from '@components/app-update/AppUpdateNavigationWatcher';
import CubeSessionGuard from '@components/auth/CubeSessionGuard';
import RootErrorBoundary from '@components/error-boundary/RootErrorBoundary';
import AppUpdateModal from '@components/app-update/AppUpdateModal';
import { releaseChunkReload } from '@utils/chunk-reload';
import { isPatchLogPreviewPage } from '@utils/patch-log-preview';
import { isApprovalLinkPage } from '@utils/approval-link-page';

// Layouts - eagerly loaded for faster initial render
import AuthLayout from './layouts/AuthLayout';
import DashboardLayout from './layouts/DashboardLayout';

// Auth Pages - eagerly loaded for faster login
import LoginPage from './app/(auth)/page';

// Frequently accessed pages - eagerly loaded
import DashboardPage from './app/(dashboard)/dashboard/page';
import BuatAjuPage from './app/(dashboard)/buat-aju/page';
// Doc Pabean is the most visited screen, so we keep it eager to remove blank flashes on hard refresh.
import DocPabeanPage from './app/(dashboard)/doc-pabean/page';

// Lazy load route components for better code splitting
const DocPabeanDraftPage = lazy(() => import('./app/(dashboard)/doc-pabean/draft/page'));
const DocPabeanEditPage = lazy(() => import('./app/(dashboard)/doc-pabean/draft/[id]/edit/page'));
const DocPabeanReviewPage = lazy(() => import('./app/(dashboard)/doc-pabean/review/[id]/page'));
const MonitoringBlokirPage = lazy(() => import('./app/(dashboard)/monitoring-blokir/page'));
const BC23EditPage = lazy(() => import('./app/(dashboard)/bc23/draft/[id]/edit/page'));
const ResponPage = lazy(() => import('./app/(dashboard)/respon/page'));
const DatabaseBarangPage = lazy(() => import('./app/(dashboard)/database-barang/page'));
const ExportPage = lazy(() => import('./app/export/page'));
const ManifestPage = lazy(() => import('./app/(dashboard)/manifest/page'));
const ProductManifestPage = lazy(() => import('./app/(dashboard)/manifest/product/page'));
const ProductManifestUploadPage = lazy(() => import('./app/(dashboard)/manifest/product/upload/page'));
const ProductManifestFromPabeanPage = lazy(() => import('./app/(dashboard)/manifest/product/from-pabean/page'));
const ProductManifestDraftEditPage = lazy(() => import('./app/(dashboard)/manifest/product/draft/[id]/edit/page'));
const ManifestReviewPage = lazy(() => import('./app/(dashboard)/manifest/review/[id]/page'));
const ManifestDraftPage = lazy(() => import('./app/(dashboard)/manifest/draft/page'));
const ManifestDocPabeanPage = lazy(() => import('./app/(dashboard)/manifest/doc-pabean/page'));
const ManifestDashboardPage = lazy(() => import('./app/(dashboard)/manifest/dashboard/page'));
const ManifestGuardianPage = lazy(() => import('./app/(dashboard)/manifest/guardian/page'));
const ManifestGuardianRadarPage = lazy(() => import('./app/(dashboard)/manifest/guardian/radar/page'));
const DraftManifestPage = lazy(() => import('./app/(dashboard)/draft-manifest/page'));
const BC20ReportPage = lazy(() => import('./app/(dashboard)/report/bc-20/page'));
const BC30ReportPage = lazy(() => import('./app/(dashboard)/report/bc-30/page'));
const BC57ReportPage = lazy(() => import('./app/(dashboard)/report/bc-57/page'));
const StampDutyPage = lazy(() => import('./app/(dashboard)/stamp-duty/page'));
const DocPabeanUploadPage = lazy(() => import('./app/(dashboard)/doc-pabean/upload/page'));
const SSMDataPage = lazy(() => import('./app/(dashboard)/ssm/data/page'));
const MiniGamesPage = lazy(() => import('./pages/MiniGamesPage'));
const ShipmentQuotaPage = lazy(() => import('./app/(dashboard)/shipment-quota/page'));
const ShipmentQuotaCreatePage = lazy(() => import('./app/(dashboard)/shipment-quota/create/page'));
const HargaReferensiPage = lazy(() => import('./app/(dashboard)/harga-referensi/page'));
const TarifTrqPage = lazy(() => import('./app/(dashboard)/tarif-trq/page'));
const PengaturanPage = lazy(() => import('./app/(dashboard)/pengaturan/page'));
const BeritaPage = lazy(() => import('./app/(dashboard)/berita/page'));
const FileCompressionPage = lazy(() => import('./app/(dashboard)/file-compression/page'));
const SplitPdfPage = lazy(() => import('./app/(dashboard)/split-pdf/page'));
const CeisaUserPage = lazy(() => import('./app/(dashboard)/master/ceisa-user/page'));
const UserPage = lazy(() => import('./app/(dashboard)/master/user/page'));
const EmailMasterPage = lazy(() => import('./app/(dashboard)/master/email/page'));
const CustomerEmailForwardingPage = lazy(() => import('./app/(dashboard)/master/customer-email-forwarding/page'));
const StatementMasterPage = lazy(() => import('./app/(dashboard)/master/statement/page'));
const ConsigneeMasterPage = lazy(() => import('./app/(dashboard)/master/consignee/page'));
const EksportirMasterPage = lazy(() => import('./app/(dashboard)/master/eksportir/page'));
const MasterBarangPage = lazy(() => import('./app/(dashboard)/master/barang/page'));
const ExportBankMasterPage = lazy(() => import('./app/(dashboard)/master/export-bank/page'));
const ManifestReminderPage = lazy(() => import('./app/(dashboard)/master/manifest-reminder/page'));
const RevokeAccessPage = lazy(() => import('./app/(dashboard)/master/revoke-access/page'));
const ManagementDashboardPage = lazy(() => import('./app/(dashboard)/management-dashboard/page'));
const CustomerApprovalPage = lazy(() => import('./app/(dashboard)/customer-approval/page'));
const ApprovalEmailGroupsPage = lazy(() => import('./app/(dashboard)/customer-approval/email-groups/page'));
const ApprovalReviewersPage = lazy(() => import('./app/(dashboard)/customer-approval/reviewers/page'));
const CustomRolesPage = lazy(() => import('./app/(dashboard)/customer-approval/roles/page'));
const CreditTopupPage = lazy(() => import('./app/(dashboard)/credit/topup/page'));
const CreditPaymentPage = lazy(() => import('./app/(dashboard)/credit/payment/page'));
const SubscriptionPage = lazy(() => import('./app/(dashboard)/subscription/page'));
const RiwayatPembaruanPage = lazy(() => import('./app/(dashboard)/riwayat-pembaruan/page'));
const SubscriptionSuccessPage = lazy(() => import('./app/(dashboard)/subscription/success/page'));

// PPFTZ (FTZ documents) module
const PpftzPage = lazy(() => import('./app/(dashboard)/ppftz/page'));
const PpftzCreatePage = lazy(() => import('./app/(dashboard)/ppftz/new/page'));
const PpftzEditPage = lazy(() => import('./app/(dashboard)/ppftz/[id]/edit/page'));
const PpftzFromProcessPage = lazy(() => import('./app/(dashboard)/ppftz/from-process/page'));

// Cukai (excise) module
const CukaiSuratJalanPage = lazy(() => import('./app/(dashboard)/cukai/surat-jalan/page'));
const CukaiDocumentsPage = lazy(() => import('./app/(dashboard)/cukai/documents/page'));
const CukaiBomPage = lazy(() => import('./app/(dashboard)/cukai/bom/page'));
const CukaiInventoryPage = lazy(() => import('./app/(dashboard)/cukai/inventory/page'));
const CukaiCompliancePage = lazy(() => import('./app/(dashboard)/cukai/compliance/page'));
const CukaiTutorialPage = lazy(() => import('./app/(dashboard)/cukai/tutorial/page'));

// Reviewer Portal (separate auth system)
const ReviewerLayout = lazy(() => import('./layouts/ReviewerLayout'));
const ReviewerLoginPage = lazy(() => import('./app/approval/login/page'));
const ReviewerDashboardPage = lazy(() => import('./app/approval/dashboard/page'));
const ReviewerReviewPage = lazy(() => import('./app/approval/review/[uuid]/page'));
const ReviewerEmailGroupsPage = lazy(() => import('./app/approval/email-groups/page'));
const ApprovalLinkPage = lazy(() => import('./app/approval/r/[token]/page'));

// Public developer docs — unlisted (reachable only via direct URL, noindex)
const PartnerApiDocsPage = lazy(() => import('./app/docs/partner-api/page'));
const PatchLogPreviewPage = lazy(() => import('./app/patch-log-preview/page'));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader size="lg" />
  </div>
);

// Styles
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/dropzone/styles.css';
import 'mantine-datatable/styles.css';

// Exported for cache invalidation on auth changes; lives in @libs/query-client.
export { queryClient };

// Inner component that uses theme context
function AppContent() {
  const { mode, colors: themeColors } = useTheme();

  // We rendered, so any chunk-reload attempt for this route succeeded. Clear
  // the guard so a later stale chunk in the same tab gets its own retry.
  useEffect(() => {
    releaseChunkReload();
  }, []);

  // Map extended theme modes to Mantine's light/dark scheme
  // Pink / horizon / dawn / mist are light themes; horizon-night / dusk are the dark companions
  const mantineColorScheme: 'light' | 'dark' =
    mode === 'cream' || mode === 'pink' || mode === 'horizon' || mode === 'dawn' || mode === 'mist' || mode === 'jarvis'
      ? 'light'
      : mode === 'blue' ||
          mode === 'navy' ||
          mode === 'purple' ||
          mode === 'green' ||
          mode === 'red' ||
          mode === 'orange' ||
          mode === 'jarvis-classic' ||
          mode === 'horizon-night' ||
          mode === 'dusk' ||
          mode === 'jarvis-night'
        ? 'dark'
        : mode;

  // Sync Mantine primary with app theme. Prefer buttonBackground for fills
  // (Horizon Night: accent teal) while primary remains for text/icons.
  // Radio/Checkbox checked fills use `--radio-color` / `--checkbox-color`.
  const brandFill = themeColors.buttonBackground || themeColors.accent || themeColors.primary;
  const brandHover = themeColors.buttonHover || themeColors.primaryHover || brandFill;
  const brandInteractive = themeColors.accent || themeColors.primary;

  // JARVIS brand modes get the full blue-600 ramp so Mantine derivations
  // (light/outline variants, text colors, hovers) stay brand-coherent.
  // Other modes keep the flat fill tuple (shade 6 = CTA fill).
  const isJarvisBrand = mode === 'jarvis' || mode === 'jarvis-night';
  const jarvisBrandRamp = [
    '#eff6ff',
    '#dbeafe',
    '#bfdbfe',
    '#93c5fd',
    '#60a5fa',
    '#3b82f6',
    '#2563eb',
    '#1d4ed8',
    '#1e40af',
    '#1e3a8a',
  ] as const;

  const theme = useMemo(
    () =>
      createTheme({
        // No `scale` here: TextScaleProvider sets html { font-size }, and Mantine's
        // sizes are rem-based, so they already grow with it. Setting scale too
        // would multiply twice (1.2 x 1.2 at the largest size).
        fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
        headings: {
          fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
        },
        colors: {
          violet: [
            '#f5f3ff',
            '#ede9fe',
            '#ddd6fe',
            '#c4b5fd',
            '#a78bfa',
            '#8b5cf6',
            '#7c3aed',
            '#6d28d9',
            '#5b21b6',
            '#4c1d95',
          ],
          // 10-shade tuple required by Mantine; filled shade (6) = CTA fill
          brand: isJarvisBrand
            ? jarvisBrandRamp
            : [
                brandFill,
                brandFill,
                brandFill,
                brandFill,
                brandHover,
                brandHover,
                brandFill,
                brandFill,
                brandFill,
                brandFill,
              ],
        },
        primaryColor: 'brand',
        defaultRadius: 'md',
        components: {
          Radio: {
            defaultProps: {
              color: brandInteractive,
            },
          },
          Checkbox: {
            defaultProps: {
              color: brandInteractive,
            },
          },
        },
      }),
    [brandFill, brandHover, brandInteractive, isJarvisBrand]
  );

  return (
    <MantineProvider theme={theme} forceColorScheme={mantineColorScheme}>
      <Notifications position="top-right" zIndex={10001} />
      {/* The CMS preview page is framed and logged out: no update prompt and
          no session guard, which could navigate the frame to /auth. */}
      {!isPatchLogPreviewPage && <AppUpdateModal />}
      <Router>
        <MFAProvider>
          {/* Nor on a reviewer's approval link: a stale JARVIS session in the
              same browser must not send the reviewer to /auth (and lose the link). */}
          {!isPatchLogPreviewPage && !isApprovalLinkPage && <CubeSessionGuard />}
          {!isPatchLogPreviewPage && <AppUpdateNavigationWatcher />}
          <Routes>
            {/* Auth Routes */}
            <Route path="/auth" element={<AuthLayout />}>
              <Route index element={<LoginPage />} />
            </Route>

            {/* Reviewer's personal link from the Kirim Approval email: no login; the token
                (after #, or in the path of an older email) plus the importir's password */}
            <Route
              path="approval/r"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ApprovalLinkPage />
                </Suspense>
              }
            />
            <Route
              path="approval/r/:token"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ApprovalLinkPage />
                </Suspense>
              }
            />

            {/* Reviewer Portal (separate auth) */}
            <Route
              path="approval/login"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ReviewerLoginPage />
                </Suspense>
              }
            />
            <Route
              path="approval"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ReviewerLayout />
                </Suspense>
              }
            >
              <Route
                path="dashboard"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ReviewerDashboardPage />
                  </Suspense>
                }
              />
              <Route
                path="review/:uuid"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ReviewerReviewPage />
                  </Suspense>
                }
              />
              <Route
                path="email-groups"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ReviewerEmailGroupsPage />
                  </Suspense>
                }
              />
            </Route>

            {/* CMS patch log preview (cms-jarvis#331): framed by the CMS, data
                by postMessage only, no auth */}
            <Route
              path="patch-log-preview"
              element={
                <Suspense fallback={<PageLoader />}>
                  <PatchLogPreviewPage />
                </Suspense>
              }
            />

            {/* Public developer docs (unlisted, no auth) */}
            <Route
              path="docs/partner-api"
              element={
                <Suspense fallback={<PageLoader />}>
                  <PartnerApiDocsPage />
                </Suspense>
              }
            />

            {/* Dashboard Routes */}
            <Route path="/" element={<DashboardLayout />}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route
                path="management-dashboard"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ManagementDashboardPage />
                  </Suspense>
                }
              />

              {/* Buat Aju Route */}
              <Route path="buat-aju" element={<BuatAjuPage />} />

              {/* Doc Pabean Routes */}
              <Route path="doc-pabean" element={<DocPabeanPage />} />
              <Route
                path="doc-pabean/upload"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <DocPabeanUploadPage />
                  </Suspense>
                }
              />
              <Route
                path="doc-pabean/draft"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <DocPabeanDraftPage />
                  </Suspense>
                }
              />
              <Route
                path="doc-pabean/draft/:id/edit"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <DocPabeanEditPage />
                  </Suspense>
                }
              />
              <Route
                path="doc-pabean/review/:id"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <DocPabeanReviewPage />
                  </Suspense>
                }
              />

              {/* PPFTZ (FTZ documents) Routes */}
              <Route
                path="ppftz"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <PpftzPage />
                  </Suspense>
                }
              />
              <Route
                path="ppftz/new"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <PpftzCreatePage />
                  </Suspense>
                }
              />
              <Route
                path="ppftz/from-process"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <PpftzFromProcessPage />
                  </Suspense>
                }
              />
              <Route
                path="ppftz/:id/edit"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <PpftzEditPage />
                  </Suspense>
                }
              />

              {/* Monitoring Blokir Route */}
              <Route
                path="monitoring-blokir"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <MonitoringBlokirPage />
                  </Suspense>
                }
              />

              {/* BC23 Routes */}
              <Route
                path="bc23/draft/:id/edit"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <BC23EditPage />
                  </Suspense>
                }
              />

              {/* Other Routes */}
              <Route
                path="respon"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ResponPage />
                  </Suspense>
                }
              />
              <Route
                path="database-barang"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <DatabaseBarangPage />
                  </Suspense>
                }
              />
              <Route
                path="manifest"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ManifestPage />
                  </Suspense>
                }
              />
              <Route
                path="manifest/product"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ProductManifestPage />
                  </Suspense>
                }
              />
              <Route
                path="manifest/product/upload"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ProductManifestUploadPage />
                  </Suspense>
                }
              />
              <Route
                path="manifest/product/from-pabean"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ProductManifestFromPabeanPage />
                  </Suspense>
                }
              />
              <Route
                path="manifest/product/draft/:id/edit"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ProductManifestDraftEditPage />
                  </Suspense>
                }
              />
              <Route
                path="manifest/dashboard"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ManifestDashboardPage />
                  </Suspense>
                }
              />
              <Route
                path="manifest/draft"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ManifestDraftPage />
                  </Suspense>
                }
              />
              <Route
                path="manifest/doc-pabean"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ManifestDocPabeanPage />
                  </Suspense>
                }
              />
              <Route
                path="manifest/guardian"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ManifestGuardianPage />
                  </Suspense>
                }
              />
              <Route
                path="manifest/guardian/radar"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ManifestGuardianRadarPage />
                  </Suspense>
                }
              />
              <Route
                path="manifest/review/:id"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ManifestReviewPage />
                  </Suspense>
                }
              />
              <Route
                path="draft-manifest"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <DraftManifestPage />
                  </Suspense>
                }
              />

              {/* Reports */}
              <Route
                path="report/bc-20"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <BC20ReportPage />
                  </Suspense>
                }
              />
              <Route
                path="report/bc-30"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <BC30ReportPage />
                  </Suspense>
                }
              />
              <Route
                path="report/bc-57"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <BC57ReportPage />
                  </Suspense>
                }
              />

              <Route
                path="stamp-duty"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <StampDutyPage />
                  </Suspense>
                }
              />

              {/* Shipment Quota Routes */}
              <Route
                path="shipment-quota"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ShipmentQuotaPage />
                  </Suspense>
                }
              />
              <Route
                path="shipment-quota/create"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ShipmentQuotaCreatePage />
                  </Suspense>
                }
              />

              {/* Harga Referensi Route */}
              <Route
                path="harga-referensi"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <HargaReferensiPage />
                  </Suspense>
                }
              />

              {/* TRQ in-quota rate master */}
              <Route
                path="tarif-trq"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <TarifTrqPage />
                  </Suspense>
                }
              />

              {/* Customer Approval Routes */}
              <Route
                path="customer-approval"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <CustomerApprovalPage />
                  </Suspense>
                }
              />
              <Route
                path="customer-approval/email-groups"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ApprovalEmailGroupsPage />
                  </Suspense>
                }
              />
              <Route
                path="customer-approval/reviewers"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ApprovalReviewersPage />
                  </Suspense>
                }
              />
              <Route
                path="customer-approval/roles"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <CustomRolesPage />
                  </Suspense>
                }
              />

              {/* Billing routes (Credit Top-up + Subscription). Hidden behind
                  VITE_BILLING_ENABLED: when off, deep links land on the
                  dashboard instead of a page the sidebar no longer offers. */}
              {BILLING_ENABLED ? (
                <>
                  <Route
                    path="credit/topup"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <CreditTopupPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="credit/payment"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <CreditPaymentPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="subscription"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <SubscriptionPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="subscription/success"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <SubscriptionSuccessPage />
                      </Suspense>
                    }
                  />
                </>
              ) : (
                <>
                  <Route path="credit/*" element={<Navigate to="/dashboard" replace />} />
                  <Route path="subscription/*" element={<Navigate to="/dashboard" replace />} />
                </>
              )}

              {/* Berita Route */}
              <Route
                path="berita"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <BeritaPage />
                  </Suspense>
                }
              />

              <Route
                path="mini-games"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <MiniGamesPage />
                  </Suspense>
                }
              />
              <Route
                path="file-compression"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <FileCompressionPage />
                  </Suspense>
                }
              />
              <Route
                path="split-pdf"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <SplitPdfPage />
                  </Suspense>
                }
              />
              <Route
                path="pengaturan"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <PengaturanPage />
                  </Suspense>
                }
              />

              {/* SSM Routes */}
              <Route
                path="ssm/data"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <SSMDataPage />
                  </Suspense>
                }
              />

              {/* Export Route */}
              <Route
                path="export"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ExportPage />
                  </Suspense>
                }
              />

              {/* Cukai Routes */}
              <Route
                path="cukai/surat-jalan"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <CukaiSuratJalanPage />
                  </Suspense>
                }
              />
              <Route
                path="cukai/documents"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <CukaiDocumentsPage />
                  </Suspense>
                }
              />
              <Route
                path="cukai/bom"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <CukaiBomPage />
                  </Suspense>
                }
              />
              <Route
                path="cukai/inventory"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <CukaiInventoryPage />
                  </Suspense>
                }
              />
              <Route
                path="cukai/compliance"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <CukaiCompliancePage />
                  </Suspense>
                }
              />
              <Route
                path="cukai/tutorial"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <CukaiTutorialPage />
                  </Suspense>
                }
              />

              {/* Master Routes */}
              <Route
                path="master/ceisa-user"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <CeisaUserPage />
                  </Suspense>
                }
              />
              <Route
                path="master/user"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <UserPage />
                  </Suspense>
                }
              />
              <Route
                path="master/email"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <EmailMasterPage />
                  </Suspense>
                }
              />
              <Route
                path="master/customer-email-forwarding"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <CustomerEmailForwardingPage />
                  </Suspense>
                }
              />
              <Route
                path="master/statement"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <StatementMasterPage />
                  </Suspense>
                }
              />
              <Route
                path="master/consignee"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ConsigneeMasterPage />
                  </Suspense>
                }
              />
              <Route
                path="master/eksportir"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <EksportirMasterPage />
                  </Suspense>
                }
              />
              <Route
                path="master/barang"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <MasterBarangPage />
                  </Suspense>
                }
              />
              <Route
                path="master/export-bank"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ExportBankMasterPage />
                  </Suspense>
                }
              />
              <Route
                path="master/manifest-reminder"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ManifestReminderPage />
                  </Suspense>
                }
              />
              <Route
                path="master/revoke-access"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <RevokeAccessPage />
                  </Suspense>
                }
              />

              {/* Published changelog — reachable by every role, so it is deliberately
                  NOT a sidebar menu entry (MenuAccessGuard gates those by role). */}
              <Route
                path="riwayat-pembaruan"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <RiwayatPembaruanPage />
                  </Suspense>
                }
              />
            </Route>

            {/* Redirect to auth if no match */}
            <Route path="*" element={<Navigate to="/auth" replace />} />
          </Routes>
        </MFAProvider>
      </Router>
    </MantineProvider>
  );
}

function App() {
  return (
    // Outermost so it also catches a crash inside the providers themselves.
    <RootErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <TextScaleProvider>
            <LanguageProvider>
              <AppContent />
            </LanguageProvider>
          </TextScaleProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </RootErrorBoundary>
  );
}

export default App;
